import { ChangeEvent, useEffect, useReducer } from 'react';

import { Draggable } from '@atom/components/common/dragAndDrop';
import FontIconButton from '@atom/components/common/FontIconButton';
import { Icon, Switch } from '@atom/mui';
import {
  addFieldOption,
  getBuilderInputValidation,
  getTypeIconData,
  MAX_CHAR_SHORT_INPUT,
  removeFieldOption,
  updateFieldOption,
} from '@atom/selectors/formSelectors';
import colors from '@atom/styles/colors';
import fontStyles from '@atom/styles/fonts';
import { AttributeGroupItem, FormFieldType } from '@atom/types/form';
import { AttributesType } from '@atom/types/inventory';
import { SchemaType } from '@atom/types/schema';

import AssetBlock from './assetField/AssetBlock';
import FieldInputs from './regularInputs/FieldInputs';
import {
  fieldContainerStateReducer,
  INIT_FIELD_STATE,
} from './FieldContainerState';

import classes from './formBuilder.module.css';

const styles = {
  icon: {
    padding: '0px',
    paddingLeft: '1rem',
    height: '40px',
    width: '40px',
  },
  toggleLabelStyles: {
    fontSize: fontStyles.md,
    color: colors.neutral.gray,
    whiteSpace: 'nowrap',
    paddingLeft: '1.5rem',
  },
  fillerStyles: {
    position: 'relative',
  },
};

interface Props {
  field: FormFieldType;
  rootSchema: SchemaType;
  id: string;
  index: number;
  deleteField: (id: string) => void;
  duplicateField: (id: string) => void;
  updateFormTemplate: (id: string, data: Object) => void;
  updateAttributeGroup: (
    fieldId: string,
    schemaId: string,
    addGroup: boolean,
    attributeGroup: AttributeGroupItem,
  ) => void;
  updateAttribute: (
    fieldId: string,
    schemaId: string,
    addAttribute: boolean,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => void;
  addAllAttributes: (
    fieldId: string,
    schemaId: string,
    attributeGroupName: string,
    attributes: AttributesType[],
  ) => void;
  removeAssetSchema: (fieldId: string, schemaId: string) => void;
}

const FIELD_TYPES_WITHOUT_UPLOAD = new Set<string>([
  'title',
  'description',
  'asset',
  'iframe',
]);

const FieldContainer = ({
  field,
  id,
  deleteField,
  duplicateField,
  index,
  rootSchema,
  updateAttributeGroup,
  updateAttribute,
  addAllAttributes,
  removeAssetSchema,
  updateFormTemplate,
}: Props) => {
  const [state, dispatch] = useReducer(
    fieldContainerStateReducer,
    INIT_FIELD_STATE,
  );

  useEffect(() => {
    dispatch({
      type: 'UPDATE_STATE',
      data: {
        ...field,
        enumValue: field.enum,
      },
    });
  }, []);

  const { text, title, description, unit, enumValue, fileUpload, url, width } =
    state;

  const onToggle = (event: any, isInputChecked: boolean) => {
    const updatedData = { [event.target.name]: isInputChecked };
    dispatch({
      type: 'UPDATE_STATE',
      data: updatedData,
    });
    updateFormTemplate(id, { ...state, ...updatedData });
  };

  const onDelete = () => deleteField(id);
  const onDuplicate = () => duplicateField(id);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isValidated = getBuilderInputValidation(
      field.type,
      event.target.name,
      event.target.value,
    );

    if (isValidated) {
      const updatedData = { [event.target.name]: event.target.value };
      dispatch({
        type: 'UPDATE_STATE',
        data: updatedData,
      });
      updateFormTemplate(id, { ...state, ...updatedData });
    }
  };

  const addOption = () => {
    const updatedData = { enumValue: addFieldOption(enumValue) };
    dispatch({
      type: 'UPDATE_STATE',
      data: updatedData,
    });
    updateFormTemplate(id, { ...state, ...updatedData });
  };

  const removeOption = (idx: number) => {
    const updatedData = { enumValue: removeFieldOption(idx, enumValue) };
    dispatch({
      type: 'UPDATE_STATE',
      data: updatedData,
    });
    updateFormTemplate(id, { ...state, ...updatedData });
  };

  const updateOption = (event: ChangeEvent<HTMLInputElement>) => {
    const newEnumValue = updateFieldOption(
      event.target.id,
      event.target.value,
      enumValue,
    );

    if (event.target.value.length <= MAX_CHAR_SHORT_INPUT) {
      const updatedData = { enumValue: newEnumValue };
      dispatch({
        type: 'UPDATE_STATE',
        data: updatedData,
      });
      updateFormTemplate(id, { ...state, ...updatedData });
    }
  };

  const leftIconData = getTypeIconData(field.type);
  const leftIconColor =
    field.type === 'asset' ? colors.brand.purple : colors.neutral.gray;

  const toggleControl = !FIELD_TYPES_WITHOUT_UPLOAD.has(field.type) ? (
    <div className={classes.toggleContainer}>
      <Switch
        sx={styles.toggleLabelStyles}
        label="File Upload"
        name="fileUpload"
        checked={fileUpload}
        onChange={onToggle}
      />
    </div>
  ) : (
    <div style={styles.fillerStyles} />
  );

  return (
    <Draggable
      key={id}
      draggableId={id}
      index={index}
      className={classes.fieldContainer}
      style={isDragging => ({
        borderColor: isDragging ? colors.neutral.ash : colors.neutral.fog,
      })}
    >
      {({ isDragging }) => (
        <>
          <div
            className={classes.fieldBoxHeader}
            style={{
              backgroundColor: isDragging
                ? colors.neutral.ash
                : colors.neutral.fog,
            }}
          >
            <div className={classes.fieldLeftIcons}>
              <Icon color={colors.neutral.ash}>drag_indicator</Icon>
              <div className={classes.typeTitleContainer}>
                <Icon color={leftIconColor}>{leftIconData.icon}</Icon>
                <div
                  className={classes.typeTitleText}
                  style={{ color: leftIconColor }}
                >
                  {leftIconData.text}
                </div>
              </div>
            </div>
            <div className={classes.fieldRightIcons}>
              {toggleControl}
              {field.type !== 'asset' && (
                <FontIconButton
                  style={styles.icon}
                  tooltip="Duplicate"
                  onClick={onDuplicate}
                  color={colors.neutral.gray}
                  icon="content_copy"
                  tooltipPosition="bottom-left"
                />
              )}
              <FontIconButton
                style={styles.icon}
                tooltip="Delete"
                onClick={onDelete}
                color={colors.neutral.gray}
                icon="delete"
                tooltipPosition="bottom-left"
              />
            </div>
          </div>
          <div className={classes.fieldContentContainer}>
            {field.type === 'asset' ? (
              <AssetBlock
                fieldId={id}
                assetSchemas={field.assetSchemas}
                rootSchema={rootSchema}
                updateAttributeGroup={updateAttributeGroup}
                updateAttribute={updateAttribute}
                addAllAttributes={addAllAttributes}
                removeAssetSchema={removeAssetSchema}
              />
            ) : (
              <FieldInputs
                field={field}
                text={text}
                title={title}
                description={description}
                unit={unit}
                enumValue={enumValue}
                url={url}
                width={width}
                onChange={onChange}
                updateOption={updateOption}
                removeOption={removeOption}
                addOption={addOption}
              />
            )}
          </div>
        </>
      )}
    </Draggable>
  );
};

export default FieldContainer;
